import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useGetUserIdParam } from '@risksmart-app/components/src/routes/routes.utils';
import { AttestationRecordStatus as Attestation_Record_Status_Enum } from '@risksmart-app/domain/src/types/consts/attestation-record-status';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ToolbarActionConfig } from 'src/blocks/object-toolbar';
import AttestationCards from 'src/components/attestations-cards';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useMarkAttestationsNotRequired } from '@/hooks/mutations/attestation';
import { useGetAttestationsRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';
import { handleError } from '@/utils/errorUtils';

import { AttestationRegisterToolbar } from '../AttestationRegisterToolbar';
import AttestationNotRequiredButton from './AttestationNotRequiredButton';
import { useGetCollectionCardProps } from './config';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'attestations',
  });

  const userId = useGetUserIdParam('userId');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  // Flag-on, the bulk "Attestation Not Required" opener is relocated to the
  // object toolbar; the stateful modal stays mounted in the page body and is
  // opened via this controlled flag (CUS-176).
  const [notRequiredOpen, setNotRequiredOpen] = useState(false);

  const { data, loading, refetch } = useGetAttestationsRegister({
    queryArgs: { userId },
  });
  const { markNotRequired } = useMarkAttestationsNotRequired();
  const { addNotification } = useNotifications();

  const pendingAttestations: { id: string; name: string }[] = useMemo(() => {
    if (loading) {
      return [];
    }

    return (
      data?.attestation_record
        ?.filter(
          (ar) =>
            ar.AttestationStatus === Attestation_Record_Status_Enum.Pending &&
            ar.CycleId !== null
        )
        .map((ar) => ({
          id: ar.Id,
          name: ar.node.documentFile?.parent?.Title ?? 'Unknown Document',
        })) || []
    );
  }, [data, loading]);

  const title = st('register_title');

  const collectionProps = useGetCollectionCardProps(data?.attestation_record);
  const counter = getCounter(collectionProps.totalItemsCount, loading);

  const handleNotRequiredAttestations = async (
    attestationRecordIds: string[]
  ) => {
    if (attestationRecordIds.length === 0) {
      return;
    }

    // The button closes its modal as soon as this resolves, so a rejection has
    // to be reported here or the failure is invisible: nothing catches it
    // upstream (no FormContext on this page) and the batch is all-or-nothing, so
    // the user would be left thinking the attestations were cleared.
    try {
      await markNotRequired({ Ids: attestationRecordIds });

      await refetch();
    } catch (error) {
      handleError(error);
      addNotification({
        type: 'error',
        content: <>{st('notRequiredModal.saveFailed')}</>,
      });
    }
  };

  // Bulk "Attestation Not Required" as a toolbar primary action (flag-on). Same
  // permission + disabled gate as the legacy button; onClick opens the modal
  // mounted below.
  const notRequiredActions = useMemo<ToolbarActionConfig[]>(
    () => [
      {
        id: 'attestation-not-required',
        label: st('notRequiredModal.buttonLabel'),
        iconName: 'check-circle',
        onClick: () => setNotRequiredOpen(true),
        disabled: pendingAttestations.length === 0,
        permission: 'update:attestation_cycle',
      },
    ],
    [st, pendingAttestations.length]
  );

  return (
    <PageLayout
      title={title}
      helpTranslationKey={'policy.registerHelp'}
      counter={counter}
      // Object-level toolbar (CUS-176) — flag-gated. Title + export for this
      // per-user card view, plus the bulk "Attestation Not Required" opener
      // relocated into the toolbar's primary slot (same permission + disabled
      // gate as the legacy button). The stateful modal is mounted in the page
      // body below and opened via `notRequiredOpen`, so it's reachable on both
      // the flag-on and flag-off paths. Flag defaults off, so the legacy actions
      // below are the live path until the flag is enabled.
      headerOverride={
        objectToolbarEnabled ? (
          <AttestationRegisterToolbar
            title={title}
            counter={counter}
            tableProps={collectionProps}
            primaryActions={notRequiredActions}
          />
        ) : undefined
      }
      actions={
        <>
          <ExportButton tableProps={collectionProps} />
          <Permission permission={'update:attestation_cycle'}>
            <AttestationNotRequiredButton
              attestations={pendingAttestations}
              loading={loading}
              onSave={handleNotRequiredAttestations}
              disabled={pendingAttestations.length === 0}
            />
          </Permission>
        </>
      }
    >
      {objectToolbarEnabled && (
        <Permission permission={'update:attestation_cycle'}>
          <AttestationNotRequiredButton
            attestations={pendingAttestations}
            loading={loading}
            onSave={handleNotRequiredAttestations}
            disabled={pendingAttestations.length === 0}
            isOpen={notRequiredOpen}
            onOpenChange={setNotRequiredOpen}
            isTriggerHidden
          />
        </Permission>
      )}
      <AttestationCards
        pagination={collectionProps.pagination}
        items={collectionProps.items}
        empty={collectionProps.empty}
        filter={collectionProps.filter}
        preferences={collectionProps.preferenceDetails.preferences}
        setPreferences={collectionProps.preferenceDetails.setPreferences}
        loading={loading}
      />
    </PageLayout>
  );
};

export default Page;
